import { VersionPill } from '@/components/admin/versioned/version-pill';
import { Button } from '@/components/ui/button';
import { RequiredMark } from '@/components/ui/required-mark';
import { Tooltip, TooltipShield } from '@/components/ui/tooltip';
import { isEditableTarget } from '@/lib/editable-target';
import { useIsVitrine } from '@/lib/use-instance';
import type { BrowserDraftStatus } from '@/lib/versioned-draft';
import type { VersionedWording } from '@/lib/versioned-labels';
import { DISCARD_DRAFT_LABEL, draftStateSentence, saveHintSentence } from '@/lib/versioned-labels';
import type { VersionSummary } from '@matter_extractor/shared';
import { History, Redo2, Undo2 } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

/**
 * The head of the editor: the name, where the pointers sit, what the draft is
 * doing, the button that saves and - since issue #108 - the one that promotes
 * (refonte modulaire §4).
 *
 * ## The save button is inactive on two grounds, and neither is an error
 *
 * §4 and §6: « inactif tant que rien n'a changé depuis la version de
 * développement, ou tant que les champs requis sont vides pour une entité
 * neuve ». Both are decided by `canSaveVersion`, pure and tested, and the
 * sentence under the button says which one is in force - a grey control with no
 * explanation is a screen that reads as broken rather than as closed.
 *
 * The comparison is structural (see `versioned-draft.ts`), so a trailing space
 * does not arm the button and a version empty of change cannot be written. The
 * server refuses that too (`no-change`); this is the courtesy in front of it.
 *
 * ## « Passer en production », next to the button that saves (issue #108)
 *
 * §4 used to read « jamais depuis l'en-tête »; `onPassToProduction` reverses
 * that, on the same régime as `onOpenHistory`: an OPTIONAL callback whose mere
 * PRESENCE draws the button, outlined rather than filled so « Enregistrer »
 * stays the screen's only plein bouton (§4). This header never decides WHEN to
 * offer it - `versioned-editor.tsx` owns that eligibility rule, including why
 * `solo` never gets one - and clicking it opens the exact `PointerDialog` the
 * history's own hover icon already opens, so no rule is decided twice.
 *
 * ## « Abandonner le brouillon », the other secondary button (issue #185)
 *
 * The same régime again: an OPTIONAL callback whose mere PRESENCE draws an
 * outlined button, between « Passer en production » and « Enregistrer ». The
 * eligibility is `versioned-editor.tsx`'s - the server holds a draft - and it
 * is NOT this header's `canSave`: the button must stay available exactly when
 * « Enregistrer » is grey because the draft differs from nothing the operator
 * can save. It lives in the `actions` row, so the folded bar carries it too.
 *
 * ## And no « Dupliquer » here (issue #123)
 *
 * §4.2 offered the duplication of an entity on the card AND in this head,
 * beside « Enregistrer »; #123 took it back out of the head on Nicolas's call
 * (2026-09-22). The gesture lives on the library card alone
 * (`versioned-card.tsx`), and the copy's editor still opens with its name
 * selected - that half is `versioned-editor.tsx`'s and never needed a button
 * here.
 *
 * ## The production pill is absent and never « v- »
 *
 * Same rule as the cards, and for the same reason (§4): the eye finds what has
 * never been passed without reading a number.
 *
 * ## It takes the four facts and not the detail
 *
 * `name`, the two pointers and the draft state, rather than the whole entity:
 * they are all this header reads, and a `detail` parameter would have made the
 * header generic in the CONTENT of an object it never looks at.
 *
 * ## Two openings the protocol lot added, and nothing else (issue #49)
 *
 * `extra` puts ONE node in the actions row, before the buttons: §7.1 hangs the
 * yellow « N problèmes » pill there, and that pill is a control listing content
 * this layer cannot read. `onOpenHistory` renders « Historique », which exists
 * because a protocol's right column is taken by the step's rules, so its three
 * panels move into a drawer (see `history-drawer.tsx`). Both are OPTIONAL and
 * both default to nothing: the criterion and the persona pass neither and emit
 * the nodes they always did, in the same order - the property `criteres.spec.ts`
 * and the capture of that screen both rest on.
 *
 * ## And a THIRD opening, the « champs » variant of the head (issue #53)
 *
 * §9.1: « le nom et la courte description de l'expérience se règlent dans
 * l'en-tête de l'éditeur, au-dessus des onglets, avec les pastilles et l'état du
 * brouillon [...] ils appartiennent à l'expérience, pas à sa timeline, et
 * restent sous les yeux quel que soit l'onglet ouvert ». So `fields` replaces
 * the `h1` with the name drawn AS a title - 20 px, bold, bleu nuit - plus one
 * optional second field the object supplies (maquette
 * `OngletHistorique.dc.html`). The identity of the screen moves to the
 * breadcrumb above, which is where the maquette puts it.
 *
 * Optional like the other two: absent, this file renders the `h1` it always
 * rendered, and the three editors that came first emit the same nodes.
 *
 * The buttons then align on the row of the FIELDS and not on the row of their
 * LABELS (revue du 2026-09-11, third pass): that is the `pt-6.5` on the actions
 * column, 20 px of label plus the 6 px gap under it. Aligned on the labels, a
 * button would sit beside a word rather than beside the control it writes.
 *
 * The COUNTER belongs to the second field alone. The name's cap is a refusal
 * the server words (`name-taken` is the one that has anything to say), while a
 * short description is a budget somebody spends while typing - the maquette
 * draws « 69 / 300 caractères » under that field and nothing under the name.
 *
 * ## And a FOURTH, the « titre et sous-titre » variant (issue #58)
 *
 * §9.1 opens the page editor « sous un fil d'Ariane et un retour Timeline », on
 * a screen that is still the experience's - the same pointers, the same draft
 * state, the same one button - while the `h1` names the PAGE and a line under
 * it names the experience the page belongs to (« Page de « Accueil PMS » »,
 * maquette `EditeurPageEcran`). Neither of the two variants above can say that:
 * the first draws the ENTITY's name and nothing else, the second draws it as a
 * control, and here the entity's name is not what the title says.
 *
 * So `heading` is a pair of nodes the consumer builds, and it REPLACES the
 * `h1`. It wins over `fields` when both are passed, which is the only honest
 * order: a screen declaring both would be asking for two titles.
 *
 * ## And a FIFTH, the « panneau » variant (issue #148)
 *
 * Réglages > Noyaux mounts a versioned object INSIDE a tab: the shell carries
 * the `h1` « Réglages », the tab its `h2`, so this head cannot be a title of
 * the page. `panel` draws it as a boxed `h3`, with « Historique » as a pill
 * beside the pastilles and, before any version, an « Aucune version » pastille
 * in their place (maquette `ReglagesNoyauxParNoyau`, `ReglagesNoyauxVide`). It
 * wins over `heading` and `fields`, which that screen passes neither of.
 *
 * ## Sticky, and folded once the page has scrolled (issue #135)
 *
 * The head carries the one button that saves, the two pastilles that say where
 * the pointers are, and - since #135 - undo and redo. All three are things
 * somebody reaches for in the middle of a long form, which is exactly where the
 * head used to be four screens above. So it STICKS, on the same token every
 * sticky surface under `/admin` anchors on (`top-admin-bar`, `styles.css`) and
 * at the same `z-20` as `layer-bar.tsx`, which is the precedent to the letter.
 *
 * Stuck whole, it would be permanent furniture: the head measures about 140 px
 * in its « champs » variant, and the administration bar above it is another 48,
 * so 190 px of every screen would stop being the screen. That is the reason for
 * the fold and not a taste for compactness. Past a scroll threshold it keeps
 * the object's NAME, the pastilles, THE STATE OF THE DRAFT and the actions row,
 * and drops the breadcrumb (which is not this component's, and simply scrolls
 * away), the title, the subtitle, the second field and the hint under the
 * buttons. About fifty pixels, which is a bar rather than a header.
 *
 * What the bar keeps is not a shortlist of what fits: it is what somebody
 * mid-gesture is relying on. The name says which object, the pastilles say
 * which version, the actions are the gestures, and the draft state is the only
 * confirmation this application ever gives that the typing is safe. A first
 * spelling of the fold dropped that last one and it was a REGRESSION, not a
 * tidier bar - the fold exists for the screens long enough to scroll, so it
 * would have removed the guarantee at exactly the moment it is needed. Five
 * unrelated e2e specs went red on it, which is what that defect looks like from
 * outside; see `foldedDraftState` below.
 *
 * The threshold is an empty SENTINEL rendered just above the head and watched
 * by an `IntersectionObserver`. A scroll listener was the other candidate and
 * was refused: it fires on every frame of every scroll, on every editor, to
 * answer a question the browser already answers by itself. The sentinel is zero
 * height and carries `mb-0`, which cancels the `space-y-6` of the editor's
 * `<section>` - without it the head would be pushed down by one gap, and a
 * marker that moves the layout is a marker that will be deleted by whoever is
 * fixing the layout.
 *
 * `IntersectionObserver` does not exist in jsdom, so the effect gives up when
 * it is missing and the head simply stays open: a component test asserts on the
 * head it always had, and nothing has to be faked in `tests/setup.ts` for
 * suites that never scroll. `tests/versioned-undo.test.tsx` poses one of its
 * own for the block that asserts what the folded bar holds.
 *
 * ## And it never folds while somebody is using it
 *
 * The scroll is one of two conditions; the other is that the head does not hold
 * the focus. A head that folds under the fingers of somebody who is typing is a
 * head that takes their caret - the « champs » variant draws the name and the
 * short description as CONTROLS, and unmounting one mid-word loses the caret and
 * the blur the rename's `PATCH` was waiting for. The rule is written on an EDITABLE
 * focus, so it covers whatever field a later lot hangs here without moving the
 * action row under a press; the reasoning sits next to `folded` in the body.
 *
 * ## And it says how much of the window it covers (issue #143)
 *
 * A sticky surface nothing makes room for is the defect `scroll-padding-top`
 * was written to prevent, and the fold above added a second one to a window
 * that had one. Everything the browser scrolls to on its own landed UNDER this
 * head: an in-page anchor, a `Tab` down a long form, the browser's own
 * find-in-page, the `scrollIntoView` the page rail calls to bring the current
 * pastille back into the frame (#140), and every Playwright action, which
 * scrolls to its target before touching it.
 *
 * So the head MEASURES itself and writes `--editor-head-height` on `:root`,
 * which `styles.css` adds to the reservation under the bar. A `ResizeObserver`
 * and not a number: the head has three variants and two states, its height
 * moves with the length of a name and with the way the action row wraps, and
 * the fold changes it in the middle of the very scroll that needs the value. A
 * height written down would be right on the screen it was measured on and
 * quietly wrong on the next.
 *
 * `:root` is global state, which is sound here because `versioned-editor.tsx`
 * is the only caller and mounts one head per screen. The cleanup takes the
 * property off, so a screen with no editor reads the `0px` fallback and
 * reserves nothing beyond the bar - the reservation follows the head rather
 * than the application. jsdom has no `ResizeObserver` either, so this effect
 * gives up exactly as the one above it does.
 */

/**
 * How far back the sentinel must come before the head OPENS again, in pixels
 * (issue #143) - the second edge of the fold's threshold, in the body below.
 *
 * It only has to be wider than what folding takes out of the flow, so that the
 * scroll anchoring that follows a fold can never on its own carry the scroll
 * back over the opening edge. The widest head is the « champs » variant, about
 * 140 px open against 52 folded; 96 clears that with room, and is small enough
 * that somebody scrolling back up gets their head back when they expect to.
 *
 * Not derived from a measurement, and that is deliberate: the shift is the
 * difference between two heights, only one of which is rendered at any moment,
 * so deriving it would mean remembering the other one - a second piece of state
 * that goes stale exactly when the head changes variant.
 */
const FOLD_REOPEN_BAND = 96;

/*
  The box of a field in the « champs » variant, and it is the criterion form's
  box to the letter (`criterion-editor-fields.tsx`) rather than a shared import:
  the layer never reaches into an object's folder, and the two are the same
  three sentences of the charte - a white ground, a gray-300 border, and
  `read-only:` where a lesser screen would have put `disabled`.
*/
const FIELD_BOX =
  'w-full rounded-md border-2 border-gray-300 bg-white read-only:bg-gray-50 read-only:text-gray-700 focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

/** One field of the head's « champs » variant (§9.1). */
export interface EditorHeaderField {
  /** « Nom de l'expérience » - the object's word, never templated here. */
  readonly label: string;
  readonly value: string;
  /** The sentence under the field, which says what the value is used for. */
  readonly hint: string;
  /** The cap of the field, and what the second field's counter counts against. */
  readonly maxLength: number;
  readonly onChange: (next: string) => void;
}

/**
 * The head in its « champs » variant: the name as a title-shaped field, plus
 * one optional field of the object's own (§9.1).
 *
 * The NAME's write is the consumer's and its régime is the layer's: this
 * component reads `name.value` and calls `name.onChange` while typing, then
 * `onNameCommit` on BLUR, which is where the `PATCH` of §4 fires. The layer
 * cannot write the name itself - a draft is generic here, and TypeScript does
 * not let `{ ...draft, name }` type back to it - so the one line that knows the
 * object's draft shape stays with the object.
 */
export interface EditorHeaderFields {
  /** The name, drawn as a title - it REPLACES the `h1`. */
  readonly name: EditorHeaderField;
  /** The rename refusal, worded from the server's `code`. Null when none. */
  readonly nameError: string | null;
  /** The name is committed on BLUR, because a rename is its own write (§4). */
  readonly onNameCommit: () => void;
  /** The object's second field: the experience's short description (§9.1). */
  readonly second?: EditorHeaderField;
  /** A version opened by « Voir », or a retired entity: nothing is typable. */
  readonly readOnly: boolean;
}

/**
 * The head's « titre et sous-titre » variant (§9.1, issue #58).
 *
 * Two nodes and not two strings: the subtitle of the page editor quotes the
 * experience's name inside a sentence (« Page de « Accueil PMS » »), and the
 * measurement reason `criterion-wording.tsx` argues at length applies here as
 * well - a name spliced into a template is one text node where the prose around
 * it is its own.
 */
export interface EditorHeading {
  readonly title: ReactNode;
  readonly subtitle: ReactNode;
}

/**
 * The undo pile, as the head draws it (issue #135).
 *
 * Two booleans and two callbacks rather than the pile itself: the head shows
 * whether each gesture is available and fires it, and the pile is generic in
 * the object's draft, which this component has never been and must not become.
 */
export interface EditorUndoRedo {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
}

/**
 * « Ctrl » or « ⌘ », read once off the platform for the two tooltips.
 *
 * `navigator.platform` is deprecated and it is still the only field served
 * everywhere this application runs; `userAgentData` is Chromium-only and
 * absent from the DOM typings this repository compiles against. The cost of
 * getting it wrong is a tooltip naming the other convention, and BOTH chords
 * are listened for anyway (`versioned-editor.tsx` reads `ctrlKey || metaKey`),
 * so a Mac whose platform string surprises us still has a working shortcut.
 */
const MODIFIER =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform)
    ? '⌘'
    : 'Ctrl';

/** What each bubble says: the gesture, then the chord that fires it. */
const UNDO_LABEL = `Annuler (${MODIFIER}+Z)`;
const REDO_LABEL = `Rétablir (${MODIFIER}+Maj+Z)`;

/*
  Off, the icon is INK and never an opacity.

  `button.tsx` argues this at length for its own `DISABLED_INERT`: an opacity
  pales a control without making it inert, and a pale filled square is more
  visible than the live ghost buttons beside it. The Button's own recipe paints
  a `gray-100` ground with a `gray-200` border, which is right for a labelled
  button and wrong for an icon in a row of ghosts - it would read as a hover
  state frozen on. So the three colours are replaced again here, by nothing at
  all plus `gray-200` ink, which is the charte's spelling of « éteint » for a
  control that has no box of its own.
*/
const ICON_OFF =
  'disabled:border-transparent disabled:bg-transparent disabled:text-gray-200 disabled:shadow-none';

export interface EditorHeaderProps {
  wording: VersionedWording;
  /** The current name, or null for an entity that does not exist yet. */
  name: string | null;
  /** Where the two pointers sit, both null before the first save. */
  production: VersionSummary | null;
  development: VersionSummary | null;
  /** What the server holds, or null when it holds no draft. */
  serverDraft: { readonly updatedAt: string; readonly updatedBy: string } | null;
  /** The draft is structurally identical to the `développement` version. */
  identical: boolean;
  /** When this editor last autosaved, or null before its first autosave. */
  autosavedAt: string | null;
  /** The number the next save will write. */
  nextNumber: number;
  canSave: boolean;
  saving: boolean;
  onSave: () => void;
  /** One node in the actions row, before the buttons: the protocol's pill (§7.1). */
  extra?: ReactNode;
  /** Renders « Historique » when the object keeps its three panels in a drawer. */
  onOpenHistory?: () => void;
  /**
   * Renders « Passer en production », outlined (issue #108). Absent: no
   * button - the régime `onOpenHistory` already sets; see `versioned-editor.tsx`
   * for the eligibility rule.
   */
  onPassToProduction?: () => void;
  /**
   * Renders « Abandonner le brouillon », outlined (issue #185). Absent: no
   * button - there is no server draft to abandon. See `versioned-editor.tsx`.
   */
  onDiscardDraft?: () => void;
  /** The « champs » variant of §9.1. Absent: the name as an `h1`. */
  fields?: EditorHeaderFields;
  /** The « titre et sous-titre » variant of §9.1. Wins over `fields` (§58). */
  heading?: EditorHeading;
  /**
   * The two icon buttons at the HEAD of the actions row (issue #135).
   *
   * Absent: no buttons, which is the régime `onOpenHistory` already sets and
   * what a read-only head gets - there is nothing to undo in a version somebody
   * is only reading, and a pair of dead icons would say otherwise.
   */
  undoRedo?: EditorUndoRedo;
  /**
   * The « panneau » variant (issue #148): the head of a versioned object that
   * lives INSIDE a tab of Réglages, where the shell already carries the `h1`
   * and the tab its `h2` (noyau.md §6.3, maquette `ReglagesNoyauxParNoyau`).
   *
   * Three things move, and nothing else. The title drops to an `h3`, in a box
   * of its own; « Historique » leaves the actions row for a small PILL beside
   * the pastilles, greyed while there is no version to read; and before the
   * first save a grey « Aucune version » pastille stands where the two
   * pointers will - a singleton has no « Nouveau ... » title to say it.
   */
  panel?: boolean;
  /**
   * Where a new entity's BROWSER draft stands, for the object that keeps one
   * (review of !462): what the line under the title says before the first
   * version - kept at a time, restored from an earlier visit, or refused.
   * Absent: the line says what it always said.
   */
  browserDraft?: BrowserDraftStatus | null;
}

export function EditorHeader({
  wording,
  name,
  production,
  development,
  serverDraft,
  identical,
  autosavedAt,
  nextNumber,
  canSave,
  saving,
  onSave,
  extra,
  onOpenHistory,
  onPassToProduction,
  onDiscardDraft,
  fields,
  heading,
  undoRedo,
  panel = false,
  browserDraft,
}: EditorHeaderProps) {
  const isNew = name === null;
  /*
    The vitrine (docs/specs/vitrine.md section 7, issue #192) reads the editor
    and writes nothing: the pastilles become the plain « v12 » the vitrine plays,
    the line about a draft is not drawn (there is none, and never will be), and
    « Enregistrer » does not exist.
  */
  const vitrine = useIsVitrine();
  /*
    Five ids, read only by the « champs » variant and computed unconditionally:
    a hook behind a branch is a hook that changes order between two renders of
    the same component, and this one has three shapes on the same screen.
  */
  const nameId = useId();
  const nameHintId = useId();
  const nameErrorId = useId();
  const secondId = useId();
  const secondHintId = useId();

  /** The empty marker above the head; it leaves the viewport as the page scrolls. */
  const sentinel = useRef<HTMLDivElement>(null);
  /** The head itself, measured so the page can make room for it (issue #143). */
  const head = useRef<HTMLElement>(null);
  /** The observer's answer, raw: has the sentinel left the viewport? */
  const [scrolledPast, setScrolledPast] = useState(false);
  /** Does this head hold the focus? Then it does not fold - see `folded` below. */
  const [focusHeld, setFocusHeld] = useState(false);

  /**
   * Folded: the page has scrolled past the sentinel AND nobody is using the
   * head (issue #135).
   *
   * The second half is not a refinement, it is the rule: a head that folds under
   * the fingers of somebody who is typing is a head that takes their caret. The
   * « champs » variant of §9.1 draws the name and the short description AS
   * CONTROLS, so folding while one of them has the focus unmounts the field
   * mid-word - no data is lost (the value rides the draft) but the caret is, and
   * the rename's `PATCH` then waits for a blur that already happened.
   *
   * On the focus and not on the field TYPE, so it holds for whatever a later lot
   * hangs here - a select, a search box, a combobox - rather than for the two
   * controls that exist today. The same idea as the full-screen preview chrome
   * of #141, which never sleeps while the focus is inside it, and for the same
   * one-line reason: a surface does not hide what somebody is using.
   *
   * DERIVED rather than a third state, and that is what answers « re-test the
   * sentinel when the focus leaves »: the observer keeps firing while the focus
   * is held - it is only its ANSWER that stops being applied - so the moment
   * `focusHeld` drops, this expression already carries the current sentinel. A
   * `folded` state patched by each of the two events would have needed the
   * measurement taken again by hand, and would have gone stale the first time
   * somebody left a field without scrolling.
   */
  const folded = scrolledPast && !focusHeld;

  /*
    An EFFECT in a repository that proscribes them, and the ban is intact: it
    forbids `useEffect` + fetch as a way of LOADING data, and this one subscribes
    to the viewport.

    Folding is free: the head sticks when its own top reaches the administration
    bar; the sentinel, zero height and immediately above it, leaves the viewport
    48 px of scroll LATER - the height of that bar. So the fold lands a beat
    after the head has stuck, which is the beat somebody needs to see that it
    stuck at all. Reading the token back out of the stylesheet to cancel that
    offset would buy a fold at the exact pixel and a second place for the value
    to be wrong.

    OPENING COSTS A BAND, and that band is a repair (issue #143). Folding takes
    about ninety pixels out of the FLOW, so the browser's scroll anchoring pulls
    the scroll up by as much to keep what one is reading still - which carries
    the sentinel back across its own threshold and opens the head, which puts
    the ninety pixels back, which crosses again. On a threshold with no width
    that is not a flicker, it is an infinite one: observed at 97 px folded and
    116 px open, the scroll going 140, 121, 140, with the editor unusable at
    that exact position. It was always reachable; #143 made it the NORM, since
    `scroll-padding-top` now parks everything the browser scrolls to just under
    a head that is about to fold.

    So the threshold has two edges: it closes where it always did, and it opens
    only once the sentinel is a full band back inside the frame. The band is
    wider than the tallest head's fold shift (the « champs » variant, about
    140 px open against 52 folded) because what has to be impossible is for the
    anchoring correction alone to carry the scroll from one edge to the other.
  */
  useEffect(() => {
    const node = sentinel.current;
    if (node === null || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (entry !== undefined) setScrolledPast(!entry.isIntersecting);
      },
      // A negative top margin shrinks the frame from the top, so the sentinel
      // only counts as back in view once it is that far down it. Applied while
      // folded ONLY: closing keeps the threshold it has always had.
      scrolledPast ? { rootMargin: `-${FOLD_REOPEN_BAND}px 0px 0px 0px` } : undefined,
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [scrolledPast]);

  /*
    The height this head covers, published for `scroll-padding-top` (issue #143).

    `getBoundingClientRect` rather than the observer's `contentRect`: the folded
    bar carries a bottom border and the « panneau » variant a two-pixel one all
    round, and a content box would under-reserve by exactly the border - which
    is the kind of miss that leaves a single line of text hidden.

    The observer is subscribed with no dependency and reads the ref at each
    call, so the fold - which swaps the children and changes the height - is
    answered by the browser instead of by a second piece of state here.
  */
  useEffect(() => {
    const node = head.current;
    if (node === null || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const observer = new ResizeObserver(() => {
      root.style.setProperty('--editor-head-height', `${node.getBoundingClientRect().height}px`);
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--editor-head-height');
    };
  }, []);

  /** The two pastilles, absent before the first save - the production one absent for longer. */
  const playedVersion = production ?? development;
  const pills =
    development === null ? null : vitrine ? (
      <span
        data-testid={`${wording.entity}-version-jouee`}
        className="font-semibold text-gray-900 text-sm leading-5"
      >
        v{playedVersion?.number}
      </span>
    ) : (
      <>
        {production === null ? null : (
          <VersionPill entity={wording.entity} tag="production" version={production.number} />
        )}
        <VersionPill entity={wording.entity} tag="development" version={development.number} />
      </>
    );

  /*
    The « panneau » row (issue #148): the pointers - or, before the first save,
    the grey pastille that says there are none - then « Historique » as a pill.
    The same row in the folded bar, so the history stays one click away.
  */
  const historyPill =
    !panel || onOpenHistory === undefined ? null : (
      // A bubble only while it is closed: open, it would repeat its own label,
      // and a bubble with no text does not open.
      <Tooltip label={development === null ? 'Aucune version à relire encore' : ''}>
        <TooltipShield closed={development === null}>
          <button
            type="button"
            data-testid={`${wording.entity}-historique-ouvrir`}
            disabled={development === null}
            onClick={onOpenHistory}
            className="inline-flex h-7.5 cursor-pointer items-center gap-1.5 rounded-pill border-2 border-bleu-nuit bg-white px-3 font-semibold text-bleu-nuit text-xs leading-none hover:bg-bleu-nuit-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-white"
          >
            <History aria-hidden="true" className="size-3.5" />
            Historique
          </button>
        </TooltipShield>
      </Tooltip>
    );
  const pillRow = panel ? (
    <>
      {pills ?? (
        <span
          data-testid={`${wording.entity}-aucune-version`}
          className="inline-flex items-center rounded-pill bg-gray-100 px-2.5 py-0.5 font-semibold text-gray-700 text-xs leading-4"
        >
          Aucune version
        </span>
      )}
      {historyPill}
    </>
  ) : (
    pills
  );

  const draftStateLine = draftStateSentence({
    isNew,
    autosavedAt,
    serverDraft,
    identical,
    developmentNumber: development?.number ?? 0,
    // OMITTED and not `undefined`, for the reason `saveHintNew` gives below.
    ...(wording.draftStateNew === undefined ? {} : { draftStateNew: wording.draftStateNew }),
    ...(browserDraft === undefined ? {} : { browserDraft }),
  });

  const draftState = vitrine ? null : (
    <p data-testid={`${wording.entity}-etat-brouillon`} className="text-gray-600 text-xs leading-4">
      {draftStateLine}
    </p>
  );

  /**
   * The same sentence, in the folded bar, and it is NOT optional (issue #135).
   *
   * The first spelling of the fold dropped it with the title and the hints, and
   * that was a regression rather than a tidier bar: « Brouillon autosauvegardé à
   * 18:42 » is the only confirmation an operator ever gets that their work is
   * safe, and the fold exists precisely for the screens long enough to scroll -
   * so it would have removed the guarantee at the moment somebody most needs it.
   * Five e2e specs that have nothing to do with this lot went red on it, which
   * is what the defect looks like from outside.
   *
   * Whole and not compacted to the clock alone, which was the other option: an
   * hour on its own does not say WHAT happened at that hour, and « Brouillon
   * identique à la v5 » carries no clock at all. The sentence is the guarantee;
   * the time is only where it is written.
   *
   * `truncate` rather than a wrap, and `min-w-0` so it can actually shrink: a
   * new entity's sentence is three lines long (`draftStateSentence`), and a bar
   * that grew to hold it would stop being a bar. The DOM text is unchanged by
   * the ellipsis, so a spec reading it still reads all of it.
   */
  const foldedDraftState = vitrine ? null : (
    <p
      data-testid={`${wording.entity}-etat-brouillon`}
      className="min-w-0 truncate text-gray-600 text-xs leading-4"
    >
      {draftStateLine}
    </p>
  );

  /**
   * The actions row, named because the folded bar renders the SAME one.
   *
   * Undo and redo come first, at the head of the row and to the left of
   * « Historique » (issue #135): they are the two gestures that undo the row
   * rather than add to it, and putting them anywhere else would have them
   * neighbour whichever button a later lot appends.
   */
  const actions = (
    <div className="flex flex-wrap items-center justify-end gap-3">
      {undoRedo === undefined ? null : (
        <>
          <HistoryIcon
            label={UNDO_LABEL}
            testId={`${wording.entity}-annuler`}
            closed={!undoRedo.canUndo}
            onClick={undoRedo.onUndo}
            icon={<Undo2 aria-hidden="true" className="size-4.5" />}
          />
          <HistoryIcon
            label={REDO_LABEL}
            testId={`${wording.entity}-retablir`}
            closed={!undoRedo.canRedo}
            onClick={undoRedo.onRedo}
            icon={<Redo2 aria-hidden="true" className="size-4.5" />}
          />
        </>
      )}
      {extra}
      {/* The « panneau » variant puts it beside the pastilles instead (#148). */}
      {onOpenHistory === undefined || panel ? null : (
        <Button
          type="button"
          variant="outline"
          data-testid={`${wording.entity}-historique-ouvrir`}
          onClick={onOpenHistory}
        >
          <History aria-hidden="true" className="size-4.5" />
          Historique
        </Button>
      )}
      {onPassToProduction === undefined ? null : (
        <Button
          type="button"
          variant="outline"
          data-testid={`${wording.entity}-passer-en-production-entete`}
          onClick={onPassToProduction}
        >
          Passer en production
        </Button>
      )}
      {onDiscardDraft === undefined ? null : (
        <Button
          type="button"
          variant="outline"
          data-testid={`${wording.entity}-abandonner-brouillon`}
          onClick={onDiscardDraft}
        >
          {DISCARD_DRAFT_LABEL}
        </Button>
      )}
      {vitrine ? null : (
        <Button
          type="button"
          data-testid={`${wording.entity}-enregistrer`}
          disabled={!canSave || saving}
          onClick={onSave}
        >
          Enregistrer une nouvelle version
        </Button>
      )}
    </div>
  );

  /*
    What the folded bar names. The « champs » variant reads the FIELD and not
    the entity: somebody renaming an experience sees the name they are typing,
    and the entity still carries the previous one until the blur commits it.
  */
  const typedName = fields === undefined ? (name ?? wording.newTitle) : fields.name.value;
  const foldedTitle: ReactNode =
    heading === undefined
      ? typedName.trim() === ''
        ? wording.newTitle
        : typedName
      : heading.title;

  /*
    ONE return for the two states, and the sentinel rendered once.

    Two returns read more easily and were refused: the observer is subscribed on
    mount to whatever `sentinel.current` held then, so the marker has to be the
    SAME DOM node before and after the fold. React would keep it here - both
    shapes are a fragment of a `div` then a `header` - but that guarantee would
    rest on two branches staying structurally twinned, which is exactly the kind
    of promise a later edit breaks silently: a sentinel replaced by a new node
    leaves the observer watching a detached one, and the head folds and never
    opens again.

    `data-testid` on the header below, and it is not decoration: the two
    pastilles of `version-pill` carry the SAME testid wherever they are
    rendered, and this screen renders them twice - once here for the pointers,
    once per history line for the version they sit on. An unscoped
    `getByTestId('critere-pastille-development')` therefore resolves two
    elements and dies on Playwright's strict mode. Scoping the head is what
    lets a spec ask « where does the pointer sit » without asking the history.
  */
  return (
    <>
      <div ref={sentinel} aria-hidden="true" className="mb-0 h-0" />
      <header
        ref={head}
        data-testid={`${wording.entity}-entete`}
        {...(folded ? { 'data-folded': 'true' } : {})}
        onFocus={(event) => {
          /*
            React's `onFocus` and `onBlur` ARE `focusin` and `focusout`: since
            React 17 both are delegated through the bubbling pair, which is why
            they can be declared once on the head rather than on every control
            it will ever hold. No listener to attach, none to release, and no
            polling of `document.activeElement` - which would answer a question
            nobody asked, on every render.

            EDITABLE targets only, and that is not a softening of the rule but its
            point. Folding MOVES the action row - it is rendered in one place
            folded and in another unfolded - so a press on one of its buttons
            would unfold the head and unmount the very button mid-click, losing
            the press. Three specs died that way: the save box never opened on
            an experience, a persona and a protocol, and the undo never fired.
            A button has no caret to lose either way, and a head that jumps open
            under the cursor is its own defect.

            The predicate is `isEditableTarget`, the same one this lot's
            keyboard shortcuts are guarded by: one rule, one place it can be
            wrong.
          */
          if (isEditableTarget(event.target)) setFocusHeld(true);
        }}
        onBlur={(event) => {
          // `relatedTarget` is where the focus is GOING. Tabbing from the name
          // field to the short description fires a blur too, and clearing on it
          // would fold the head between two of its own controls. Null - the
          // focus falling back to the document - reads as « gone », correctly.
          if (!event.currentTarget.contains(event.relatedTarget)) setFocusHeld(false);
        }}
        className={
          folded
            ? 'sticky top-admin-bar z-20 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-gray-200 border-b bg-white py-2'
            : panel
              ? // The « panneau » box of the maquette (#148): the head of an
                // object that shares its tab with nothing else above it.
                'sticky top-admin-bar z-20 flex flex-wrap items-start justify-between gap-6 rounded-lg border-2 border-gray-200 bg-white px-5 py-4.5'
              : 'sticky top-admin-bar z-20 flex flex-wrap items-start justify-between gap-6 bg-white'
        }
      >
        {folded ? (
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            {/* `truncate` and not a wrap: the bar is fifty pixels, and a long
                name allowed on to a second line would make it a hundred -
                which is the furniture the fold exists to remove. */}
            <p className="min-w-0 truncate font-bold text-bleu-nuit text-lg">{foldedTitle}</p>
            {pillRow}
            {foldedDraftState}
          </div>
        ) : panel ? (
          <div className="min-w-0 space-y-2">
            <h3 className="break-words font-bold text-bleu-nuit text-xl">
              {name ?? wording.newTitle}
            </h3>
            <div className="flex flex-wrap items-center gap-2">{pillRow}</div>
            {draftState}
          </div>
        ) : heading !== undefined ? (
          <div className="min-w-0 space-y-2">
            <h1 className="break-words font-bold text-3xl text-bleu-nuit">{heading.title}</h1>
            <p className="break-words text-gray-700 text-sm">{heading.subtitle}</p>
            {pills === null ? null : (
              <div className="flex flex-wrap items-center gap-2">{pills}</div>
            )}
            {draftState}
          </div>
        ) : fields === undefined ? (
          <div className="min-w-0 space-y-2">
            <h1 className="break-words font-bold text-3xl text-bleu-nuit">
              {name ?? wording.newTitle}
            </h1>
            {pills === null ? null : (
              <div className="flex flex-wrap items-center gap-2">{pills}</div>
            )}
            {draftState}
          </div>
        ) : (
          <div className="min-w-0 flex-1 space-y-3">
            <div
              className={
                fields.second === undefined
                  ? 'max-w-90'
                  : 'grid items-start gap-4 md:grid-cols-[minmax(0,360px)_minmax(0,1fr)]'
              }
            >
              <div className="min-w-0 space-y-1.5">
                <label htmlFor={nameId} className="block font-semibold text-gray-800 text-sm">
                  {fields.name.label}
                  <RequiredMark />
                </label>
                {/* Drawn as a title and typed as a field: 20 px, bold, bleu nuit
                  (§9.1). It is what replaces the `h1`, and `read-only:` greys
                  the whole of it rather than `disabled`, for the reason
                  `criterion-editor-fields` gives - a version opened by « Voir »
                  must stay selectable. */}
                <input
                  id={nameId}
                  data-testid={`${wording.entity}-nom`}
                  value={fields.name.value}
                  readOnly={fields.readOnly}
                  required
                  aria-required="true"
                  aria-invalid={fields.nameError !== null}
                  // The hint or the refusal, never neither: a rule painted only
                  // beside a field is no rule for whoever cannot see it.
                  aria-describedby={fields.nameError === null ? nameHintId : nameErrorId}
                  maxLength={fields.name.maxLength}
                  onChange={(event) => fields.name.onChange(event.currentTarget.value)}
                  onBlur={fields.onNameCommit}
                  className={`${FIELD_BOX} h-11 px-3 font-bold text-bleu-nuit text-xl`}
                />
                {fields.nameError === null ? (
                  <p id={nameHintId} className="text-gray-600 text-xs leading-4">
                    {fields.name.hint}
                  </p>
                ) : (
                  <p id={nameErrorId} role="alert" className="text-rouge-deep text-xs leading-4">
                    {fields.nameError}
                  </p>
                )}
              </div>

              {fields.second === undefined ? null : (
                <div className="min-w-0 space-y-1.5">
                  <label htmlFor={secondId} className="block font-semibold text-gray-800 text-sm">
                    {fields.second.label}
                  </label>
                  <input
                    id={secondId}
                    data-testid={`${wording.entity}-entete-champ`}
                    value={fields.second.value}
                    readOnly={fields.readOnly}
                    aria-describedby={secondHintId}
                    maxLength={fields.second.maxLength}
                    onChange={(event) => fields.second?.onChange(event.currentTarget.value)}
                    className={`${FIELD_BOX} h-11 px-3 text-base`}
                  />
                  <div className="flex items-baseline justify-between gap-3">
                    <p id={secondHintId} className="text-gray-600 text-xs leading-4">
                      {fields.second.hint}
                    </p>
                    {/* The budget as it is spent, and `aria-hidden` because the
                      field already carries `maxLength`: a screen reader that
                      read the count on every keystroke would read nothing
                      else. */}
                    <p
                      aria-hidden="true"
                      className="shrink-0 whitespace-nowrap text-gray-500 text-xs leading-4"
                    >
                      {fields.second.value.length} / {fields.second.maxLength} caractères
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              {pills}
              {draftState}
            </div>
          </div>
        )}

        {/*
          `min-w-0` and NOT `shrink-0`, and the difference is a phone.

          `shrink-0` pins this column at the intrinsic width of its widest child,
          which for a criterion is a short sentence and for a protocol is « Actif
          dès que le nom, la description et le plafond de chaque étape sont
          remplis. » - 393 px of unwrappable column on a 390 px screen, which
          scrolled the whole body sideways (`admin-responsive.spec.ts`). Allowed
          to shrink, the column wraps its sentence and the button keeps its own
          `whitespace-nowrap`, which is what actually had to be protected.

          Folded, the column is gone and the row stands on its own: the sentence
          under it is the « phrase d'aide » the fold drops, and a one-line bar
          carrying a two-line explanation would not be a one-line bar.
        */}
        {folded ? null : (
          <div
            className={`flex min-w-0 flex-col items-end gap-1.5${
              // The labels row exists only in the « champs » variant, so only it
              // pushes the buttons down by one (revue du 2026-09-11, third pass).
              fields === undefined || heading !== undefined ? '' : ' pt-6.5'
            }`}
          >
            {actions}
            {/* No save on the vitrine, so no sentence about what a save would do. */}
            {vitrine ? null : (
              <p className="text-right text-gray-600 text-xs leading-4">
                {saveHintSentence({
                  isNew,
                  nextNumber,
                  developmentNumber: development?.number ?? 0,
                  // OMITTED and not `undefined`: `exactOptionalPropertyTypes` makes
                  // the two different types, and an optional field means absent.
                  ...(wording.saveHintNew === undefined
                    ? {}
                    : { saveHintNew: wording.saveHintNew }),
                })}
              </p>
            )}
          </div>
        )}
        {folded ? actions : null}
      </header>
    </>
  );
}

/**
 * One of the two history icons, off in `gray-200` ink rather than in opacity.
 *
 * A local component and not two copies at the call site: undo and redo differ
 * by an icon, a sentence and a callback, and what they SHARE is the part a
 * second copy gets wrong - a `disabled` control receives neither pointer nor
 * focus, so its bubble has to hang on a shield rather than on the button
 * (`TooltipShield`, and the same idiom `duplicate-button.tsx` already carries).
 *
 * The bubble repeats the accessible name deliberately: an icon button has no
 * visible label, so the name lives in `aria-label` and the bubble is the only
 * place a mouse ever reads it. `Tooltip` drops the `aria-describedby` it would
 * otherwise set when the two strings match, so nothing is announced twice.
 */
function HistoryIcon({
  label,
  testId,
  closed,
  onClick,
  icon,
}: {
  readonly label: string;
  readonly testId: string;
  /** Nothing to undo, or nothing to redo: the icon is off, never absent. */
  readonly closed: boolean;
  readonly onClick: () => void;
  readonly icon: ReactNode;
}) {
  const control = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      data-testid={testId}
      className={ICON_OFF}
      disabled={closed}
      onClick={onClick}
    >
      {icon}
    </Button>
  );
  return (
    <Tooltip label={label}>
      {closed ? <TooltipShield closed>{control}</TooltipShield> : control}
    </Tooltip>
  );
}
